// The Strategy tab's library: the documents and web links the strategy is built from, each with labels
// (institution, type, the document's own date, keywords) and the source of the content it is.
//   files    the library folder's (STRATEGY_LIBRARY, functions/src/folders): their labels under `f:<fileId>`
//   links    web links, whole under `l:<id>`: address, title, labels
//   removed  `gone:<source>`: the last document of a source the strategy cites was deleted (what, who, when).
//            The views mark that source until a document of it is back.
// All in the changes' section "library" (domain/strategy.ts): saved key by key, as the views' changes are.
// What a source gave the strategy is read from the content (contributionOf): every element names its source
// (external.source, a project's evidence[0]), so it can be counted, and compared between content versions.
import { savedConsolidation } from './consolidate.ts';
import type { StrategyState } from './strategy.ts';

export const LIBRARY_TYPES = ['strategy', 'plan', 'analysis', 'minutes', 'presentation', 'law', 'data', 'other'] as const;
export type LibraryType = (typeof LIBRARY_TYPES)[number];

/** A document's labels. `src`: the content source it is (sources[].id), or one not read into the content yet. */
export interface LibraryLabels {
  inst?: string;
  type?: LibraryType;
  /** The document's own date: 2026, 2026-10 or 2026-10-15. */
  date?: string;
  kw?: string[];
  src?: string;
}

/** A web link in the library. */
export interface LibraryLink extends LibraryLabels {
  url: string;
  title: string;
  by?: string;
  at?: string;
}

/** A source whose last document was deleted while the strategy cites it. */
export interface LibraryGone {
  src: string;
  /** The document deleted last. */
  name: string;
  by?: string;
  at: string;
}

export const fileLabelKey = (fileId: string): string => `f:${fileId}`;
export const linkKey = (id: string): string => `l:${id}`;
export const goneKey = (src: string): string => `gone:${src}`;

export const LIBRARY_URL_MAX = 2000;
export const LIBRARY_TITLE_MAX = 300;
export const LIBRARY_KEYWORDS_MAX = 20;
const LABEL_MAX = 60;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DATE = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const s = (v: unknown): string => (v == null ? '' : String(v));

/** An address the library may link to: http or https, nothing else (no javascript:, no data:). */
export function isWebAddress(url: string): boolean {
  if (url.length > LIBRARY_URL_MAX) return false;
  try {
    const u = new URL(url);
    return (u.protocol === 'https:' || u.protocol === 'http:') && !!u.hostname;
  } catch {
    return false;
  }
}

function labelsProblem(v: Obj): string {
  for (const k of ['inst', 'src'] as const) {
    if (v[k] !== undefined && (typeof v[k] !== 'string' || (v[k] as string).length > LABEL_MAX)) return `“${k}” is a text of at most ${LABEL_MAX} characters.`;
  }
  if (v.type !== undefined && !(LIBRARY_TYPES as readonly unknown[]).includes(v.type)) return `Unknown type: ${s(v.type)}`;
  if (v.date !== undefined && (typeof v.date !== 'string' || (v.date !== '' && !DATE.test(v.date)))) return 'A date is 2026, 2026-10 or 2026-10-15.';
  if (v.kw !== undefined) {
    if (!Array.isArray(v.kw) || v.kw.length > LIBRARY_KEYWORDS_MAX) return `At most ${LIBRARY_KEYWORDS_MAX} keywords.`;
    if (v.kw.some((k) => typeof k !== 'string' || !k.trim() || k.length > LABEL_MAX)) return `A keyword is a text of at most ${LABEL_MAX} characters.`;
  }
  return '';
}

/** Why a change of the section "library" cannot be stored, or ''. A link's address must be http(s): it becomes a link on the page. */
export function libraryEntryProblem(key: string, value: unknown): string {
  if (value === null) return '';
  if (!isObj(value)) return 'A library entry is an object.';
  if (key.startsWith('f:')) {
    if (!UUID.test(key.slice(2))) return 'Unknown file.';
    return labelsProblem(value);
  }
  if (key.startsWith('l:')) {
    if (!UUID.test(key.slice(2))) return 'Unknown link.';
    if (typeof value.url !== 'string' || !isWebAddress(value.url)) return 'A link is a web address starting with https:// (or http://).';
    if (typeof value.title !== 'string' || !value.title.trim() || value.title.length > LIBRARY_TITLE_MAX) return `A link needs a title of at most ${LIBRARY_TITLE_MAX} characters.`;
    return labelsProblem(value);
  }
  if (key.startsWith('gone:')) {
    if (typeof value.src !== 'string' || value.src !== key.slice(5)) return 'A removed source names itself.';
    if (typeof value.name !== 'string' || typeof value.at !== 'string') return 'A removed source says what was deleted, and when.';
    return '';
  }
  return 'Unknown library entry.';
}

// ── the content's sources ───────────────────────────────────────────────────

/** A source of the content, as the views' Sources tab lists it. */
export interface StrategySource {
  id: string;
  title: string;
  titleEn: string;
  /** institution, unit, national, EU */
  level: string;
  date: string;
  path: string;
}

export function sourcesOf(content: Obj): StrategySource[] {
  const list = Array.isArray(content.sources) ? content.sources.filter(isObj) : [];
  return list
    .map((x) => ({ id: s(x.id), title: s(x.title), titleEn: isObj(x.en) ? s(x.en.title) : '', level: s(x.level), date: s(x.date), path: s(x.path) }))
    .filter((x) => x.id);
}

/** An institution a document can belong to. `level` orders them: units, institutions, national, EU. */
export interface LibraryInstitution {
  id: string;
  level: string;
}

const LEVEL_ORDER = ['unit', 'institution', 'national', 'EU'];

/**
 * The institutions of the content: its sources (a source named after another, VULSK-AN, is that one's), the
 * units projects belong to, the institutions people added in the views.
 */
export function libraryInstitutions(content: Obj, state: StrategyState): LibraryInstitution[] {
  const out = new Map<string, string>();
  const srcs = sourcesOf(content);
  const ids = srcs.map((x) => x.id);
  for (const x of srcs) if (!ownerIn(x.id, ids, true)) out.set(x.id, x.level);
  for (const p of Array.isArray(content.projects) ? content.projects.filter(isObj) : []) if (s(p.unit) && !out.has(s(p.unit))) out.set(s(p.unit), 'unit');
  for (const i of Object.values(state.institutions ?? {})) if (isObj(i) && s(i.id) && !out.has(s(i.id))) out.set(s(i.id), s(i.level) || 'institution');
  const rank = (l: string) => (LEVEL_ORDER.includes(l) ? LEVEL_ORDER.indexOf(l) : LEVEL_ORDER.length);
  return [...out].map(([id, level]) => ({ id, level })).sort((a, b) => rank(a.level) - rank(b.level));
}

/** The institution `id` is part of: itself, or the one it is named after ("VULSK-AN" → "VULSK"). `strict`: another one only. */
function ownerIn(id: string, ids: readonly string[], strict = false): string {
  const owner = ids.filter((o) => o !== id && (id.startsWith(`${o}-`) || id.startsWith(`${o} `))).sort((a, b) => b.length - a.length)[0];
  return owner ?? (strict ? '' : id);
}

/** The institution of a source. */
export const institutionOfSource = (src: string, insts: readonly LibraryInstitution[]): string => {
  const ids = insts.map((i) => i.id);
  return ids.includes(src) ? src : ownerIn(src, ids);
};

// ── guessing a new document's labels ────────────────────────────────────────

/** Lower case, no accents (ą → a), words split on anything else: "Santaros_strategija" → "santaros strategija". */
export function plainWords(text: string): string[] {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** A full date in a name: 2026-10-15, 2026 10 15, 20261015 → "2026-10-15"; else ''. */
export function dateInName(name: string): string {
  const m = /(?:^|\D)(20\d\d)[-_. ]?(0[1-9]|1[0-2])[-_. ]?(0[1-9]|[12]\d|3[01])(?:\D|$)/.exec(name);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

/** Other names an institution goes by in file names and addresses (plain words). */
const ALIASES: Record<string, string[]> = {
  VULSK: ['vulsk', 'santaros', 'santaros klinikos'],
  'VU MF': ['vu mf', 'medicinos fakultetas'],
  CDM: ['cdm', 'smc', 'skaitmenines medicinos'],
  VPC: ['vpc', 'ncp', 'patologijos centras'],
  LMC: ['lmc'],
  NAT: ['e tar', 'etar', 'lrs', 'sam', 'lrv'],
  EU: ['eur lex', 'europa eu', 'ehds'],
};

/** The institution a name mentions (its id, or an alias), else ''. */
function institutionIn(text: string, insts: readonly LibraryInstitution[]): string {
  const words = ` ${plainWords(text).join(' ')} `;
  for (const i of insts) {
    for (const alias of [plainWords(i.id).join(' '), ...(Object.hasOwn(ALIASES, i.id) ? ALIASES[i.id]! : [])]) if (alias && words.includes(` ${alias} `)) return i.id;
  }
  return '';
}

/** Words in a name that say what the document is, checked in this order (a meeting about the strategy is minutes). */
const TYPE_WORDS: [LibraryType, RegExp][] = [
  ['minutes', /aptarim|protokol|minutes|posedz|susitikim|meeting/],
  ['analysis', /analiz|analysis|vertinim|audit|review/],
  ['law', /istatym|isakym|nutarim|reglament|direktyv|regulation|directive|\blaw\b/],
  ['strategy', /strateg|\bsvp\b/],
  ['plan', /projekt|\bplan|program|veiksm/],
];

/** What a name says the document is: its words, else its extension (a deck, a table); else none. */
export function typeInName(name: string): LibraryType | undefined {
  const words = plainWords(name).join(' ');
  for (const [type, re] of TYPE_WORDS) if (re.test(words)) return type;
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? '';
  if (['ppt', 'pptx', 'key', 'odp'].includes(ext)) return 'presentation';
  if (['xls', 'xlsx', 'csv', 'ods', 'json'].includes(ext)) return 'data';
  return undefined;
}

/** Words that tell nothing apart. */
const STOP = new Set(['ir', 'and', 'the', 'of', 'for', 'inputs', 'docx', 'pdf', 'xlsx', 'pptx', 'doc', 'xls', 'ppt', 'dalis', 'part']);
const meaningful = (text: string) => new Set(plainWords(text).filter((w) => w.length >= 3 && !/^\d+$/.test(w) && !STOP.has(w) && !/^v\d+$/.test(w)));
const baseName = (path: string) => path.split(/[\\/]/).pop() ?? '';

/**
 * The source a document is, by its name: the file a source was read from (its path), else the source of its
 * date, else the one sharing the most words with it (two at least, and more than any other). '' when unsure:
 * a wrong source would count the wrong document's contribution.
 */
/** The document is the very file a source was read from (its path ends in the document's name). */
const isSourceFile = (name: string, x: StrategySource): boolean => !!x.path && plainWords(baseName(x.path)).join(' ') === plainWords(name).join(' ');

export function guessSource(name: string, sources: readonly StrategySource[]): string {
  const byFile = sources.filter((x) => isSourceFile(name, x));
  if (byFile.length === 1) return byFile[0]!.id;
  const date = dateInName(name);
  const byDate = date ? sources.filter((x) => x.date === date) : [];
  if (byDate.length === 1) return byDate[0]!.id;
  const mine = meaningful(name);
  const scored = sources
    .map((x) => {
      const theirs = meaningful(`${x.title} ${x.titleEn} ${x.path}`);
      return { id: x.id, n: [...mine].filter((w) => theirs.has(w)).length };
    })
    .sort((a, b) => b.n - a.n);
  const [best, next] = scored;
  return best && best.n >= 2 && best.n > (next?.n ?? 0) ? best.id : '';
}

/** Labels for a document no one has labelled: from its name (and a link's address). Shown as guesses until someone keeps them. */
/** A link's own labels: all its fields but its address, title, who added it and when. */
export function linkLabels(l: LibraryLink & { id?: string }): LibraryLabels {
  const { url: _url, title: _title, by: _by, at: _at, id: _id, ...own } = l;
  return own;
}

/** What a link's labels are guessed from: its title and its address. */
export const linkGuessText = (l: { title: string; url: string }): string => `${l.title} ${l.url}`;

/** A document's labels as the library window shows them: the ones people kept, else the guess from `guessFrom`. */
export function labelsOrGuess(saved: LibraryLabels | undefined, guessFrom: string, content: Obj, state: StrategyState): { labels: LibraryLabels; guessed: boolean } {
  const guessed = !saved || !Object.keys(saved).length;
  return { labels: guessed ? guessLabels(guessFrom, content, state) : saved, guessed };
}

export function guessLabels(name: string, content: Obj, state: StrategyState): LibraryLabels {
  const insts = libraryInstitutions(content, state);
  const srcs = sourcesOf(content);
  const src = guessSource(name, srcs);
  const inst = src ? institutionOfSource(src, insts) : institutionIn(name, insts);
  // A source's date is the document's only when the document is that source's file.
  const source = srcs.find((x) => x.id === src && isSourceFile(name, x));
  const date = dateInName(name) || (source && DATE.test(source.date) ? source.date : '');
  const type = typeInName(name);
  return { ...(inst ? { inst } : {}), ...(type ? { type } : {}), ...(date ? { date } : {}), ...(src ? { src } : {}) };
}

// ── what a source gave the strategy ─────────────────────────────────────────

export interface Contribution {
  /** Its elements in the content (people's deletions left out), by id. */
  ids: string[];
  /** How many of each kind: goal, measure, programme, project, direction… */
  kinds: Record<string, number>;
  /** Links with one of its elements at either end (people's own too; rejected ones left out). */
  links: number;
  /** Where in it the projects' evidence is: "skaidrės 11, 28". */
  places: string[];
  /** Consolidated projects built on it: a subproject is one of its elements, or linked to one. */
  feeds: { inst: string; code: string }[];
}

/** Each element's source: external.source; a project's evidence[0] when a source, else its unit; a direction's unit. */
export function elementSources(content: Obj): Map<string, { src: string; kind: string; place: string }> {
  const ids = new Set(sourcesOf(content).map((x) => x.id));
  const out = new Map<string, { src: string; kind: string; place: string }>();
  const list = (k: string) => (Array.isArray(content[k]) ? (content[k] as unknown[]).filter(isObj) : []);
  for (const x of list('external')) out.set(s(x.id), { src: s(x.source), kind: s(x.kind) || 'element', place: s(x.loc) });
  for (const p of list('projects')) {
    const ev = Array.isArray(p.evidence) ? p.evidence.map(s) : [];
    const src = ev[0] && ids.has(ev[0]) ? ev[0] : s(p.unit);
    out.set(s(p.id), { src, kind: 'project', place: src === ev[0] ? (ev[1] ?? '') : '' });
  }
  for (const d of list('directions')) out.set(s(d.id), { src: s(d.unit), kind: 'direction', place: '' });
  return out;
}

/** What each source gave the strategy (sources with nothing are left out). */
export function contributionOf(content: Obj, state: StrategyState): Map<string, Contribution> {
  const of = elementSources(content);
  const deleted = (id: string) => isObj(state.edits?.[id]) && !!(state.edits[id] as Obj).deleted;
  const out = new Map<string, Contribution>();
  const get = (src: string) => {
    let c = out.get(src);
    if (!c) out.set(src, (c = { ids: [], kinds: Object.create(null), links: 0, places: [], feeds: [] }));
    return c;
  };
  for (const [id, e] of of) {
    if (!e.src || deleted(id)) continue;
    const c = get(e.src);
    c.ids.push(id);
    c.kinds[e.kind] = (c.kinds[e.kind] ?? 0) + 1;
    if (e.kind === 'project' && e.place && !c.places.includes(e.place)) c.places.push(e.place);
  }
  // Links: the content's and people's own, not rejected.
  const rels = [...(Array.isArray(content.relations) ? content.relations : []), ...Object.values(state.links ?? {})].filter(isObj);
  const near = new Map<string, Set<string>>();
  for (const r of rels) {
    const a = s(r.from_ ?? r.from);
    const b = s(r.to);
    if (!a || !b || deleted(a) || deleted(b) || (state.decisions?.[s(r.id)] as Obj | undefined)?.s === 'rejected') continue;
    const sa = of.get(a)?.src;
    const sb = of.get(b)?.src;
    for (const src of new Set([sa, sb])) if (src && out.has(src)) out.get(src)!.links++;
    if (!near.has(a)) near.set(a, new Set());
    if (!near.has(b)) near.set(b, new Set());
    near.get(a)!.add(b);
    near.get(b)!.add(a);
  }
  // Consolidated projects: a subproject that is one of a source's elements, or linked to one.
  const insts = Object.keys(state.consolidation ?? {}).filter((k) => !k.includes('|'));
  for (const inst of insts) {
    for (const p of savedConsolidation(state, inst)?.projects ?? []) {
      const srcs = new Set<string>();
      for (const sub of p.subs) {
        if (!sub.ref || deleted(sub.ref)) continue;
        for (const id of [sub.ref, ...(near.get(sub.ref) ?? [])]) {
          const src = of.get(id)?.src;
          if (src && out.has(src)) srcs.add(src);
        }
      }
      for (const src of srcs) out.get(src)!.feeds.push({ inst, code: p.code });
    }
  }
  return out;
}

/** Each source's element ids in a content: what a version is compared by (versionChanges). */
export function sourceElements(content: Obj): Record<string, string[]> {
  // No prototype: a source may be called "constructor".
  const out: Record<string, string[]> = Object.create(null);
  for (const [id, e] of elementSources(content)) if (e.src) (out[e.src] ??= []).push(id);
  return out;
}

/** What each source added and took away from one version to the next. Sources with no change are left out. */
export function versionChanges(before: Record<string, string[]>, after: Record<string, string[]>): Record<string, { added: string[]; removed: string[] }> {
  const out: Record<string, { added: string[]; removed: string[] }> = Object.create(null);
  for (const src of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const was = new Set(Object.hasOwn(before, src) ? before[src]! : []);
    const now = new Set(Object.hasOwn(after, src) ? after[src]! : []);
    const added = [...now].filter((id) => !was.has(id));
    const removed = [...was].filter((id) => !now.has(id));
    if (added.length || removed.length) out[src] = { added, removed };
  }
  return out;
}

// ── a document's place in the strategy ──────────────────────────────────────

/**
 *   cited     its source gives the strategy elements
 *   listed    its source is in the content, but nothing there names it
 *   unread    its source is not in the content yet: a Strategy Integrator run has not read it
 *   unlinked  no source: nobody said which it is
 */
export type LibraryStatus = 'cited' | 'listed' | 'unread' | 'unlinked';

export function libraryStatus(src: string | undefined, sources: readonly StrategySource[], contribution: Map<string, Contribution>): LibraryStatus {
  if (!src) return 'unlinked';
  if (contribution.get(src)?.ids.length) return 'cited';
  return sources.some((x) => x.id === src) ? 'listed' : 'unread';
}

/** The library's entries, from the changes' section: file labels by file id, links by id, removed sources by source. */
export function libraryEntries(state: StrategyState): { files: Map<string, LibraryLabels>; links: (LibraryLink & { id: string })[]; gone: Map<string, LibraryGone> } {
  const files = new Map<string, LibraryLabels>();
  const links: (LibraryLink & { id: string })[] = [];
  const gone = new Map<string, LibraryGone>();
  for (const [key, v] of Object.entries(state.library ?? {})) {
    if (!isObj(v) || libraryEntryProblem(key, v)) continue;
    if (key.startsWith('f:')) files.set(key.slice(2), v as LibraryLabels);
    else if (key.startsWith('l:')) links.push({ ...(v as unknown as LibraryLink), id: key.slice(2) });
    else if (key.startsWith('gone:')) gone.set(key.slice(5), v as unknown as LibraryGone);
  }
  links.sort((a, b) => s(b.at).localeCompare(s(a.at)));
  return { files, links, gone };
}
